import { styled } from '@mui/material/styles';
import { DialogSelectTrigger } from 'components/ui';

export const DashboardSelectTrigger = styled(DialogSelectTrigger)(({ theme }) => ({
  '&&': {
    borderRadius: 'var(--dialog-select-trigger-radius)',
    fontSize: 'var(--dialog-description-font-size)',
    lineHeight: 'var(--dialog-description-line-height)',
    minWidth: 0,
    width: '100%',
  },
  '& [class*="DialogSelectTriggerValue"]': {
    minWidth: 0,
  },
  '&:hover:not(:disabled)': {
    backgroundColor: theme.palette.neutral[50],
  },
}));

export const DashboardControlStack = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(1),
  minWidth: 0,
}));

export const DashboardControlRow = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.spacing(1),
  minWidth: 0,
}));

// The drag handle, the growing controls, and the delete button. `minWidth: 0` on the middle cell is
// what lets a long metric label ellipsise instead of forcing the row wider than the dialog.
export const DashboardSortableRowRoot = styled('div', {
  shouldForwardProp: prop => prop !== 'isDragging',
})<{ isDragging?: boolean }>(({ isDragging, theme }) => ({
  alignItems: 'center',
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.neutral[200]}`,
  borderRadius: theme.shape.borderRadius,
  display: 'grid',
  gap: theme.spacing(1),
  gridTemplateColumns: 'auto minmax(0, 1fr) auto',
  opacity: isDragging ? 0 : 1,
  padding: theme.spacing(0.5, 1),
}));

export const DashboardDragHandle = styled('span')(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.neutral[400],
  cursor: 'grab',
  display: 'flex',
  touchAction: 'none',
  '&:active': {
    cursor: 'grabbing',
  },
  '&[aria-disabled="true"]': {
    cursor: 'default',
    color: theme.palette.neutral[300],
  },
}));

export const DashboardChipList = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  minWidth: 0,
}));

export const DashboardEmptyHint = styled('p')(({ theme }) => ({
  color: theme.palette.neutral[500],
  fontSize: 'var(--dialog-description-font-size)',
  lineHeight: 'var(--dialog-description-line-height)',
  margin: 0,
}));

// A native <input type="color">, sized to sit next to the hex field rather than at TextField height.
// Native rather than a bespoke popover: the browser's own picker is the accessible, dependency-free
// one, and ColorSelector under dashboard409a already reaches for it.
export const DashboardColorSwatch = styled('input')(({ theme }) => ({
  appearance: 'none',
  background: 'none',
  border: `1px solid ${theme.palette.neutral[200]}`,
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  flex: '0 0 auto',
  height: 34,
  padding: 2,
  width: 44,
  '&:disabled': {
    cursor: 'default',
    opacity: 0.5,
  },
  // Without these the swatch keeps the UA's own inset border and reads as a second, misaligned box.
  '&::-webkit-color-swatch-wrapper': {
    padding: 0,
  },
  '&::-webkit-color-swatch': {
    border: 'none',
    borderRadius: 2,
  },
  '&::-moz-color-swatch': {
    border: 'none',
    borderRadius: 2,
  },
}));
